/**
 * Pre-Print Frontend Hooks
 *
 * tRPC query/mutation hooks for the Pre-Print page.
 * Wraps backend endpoints with caching, invalidation, and toast notifications.
 *
 * Hooks:
 * - useProductSearch: debounced product search via listProductsGrouped
 * - useBlankSkuMatch: auto-match blank garment SKU for a product SKU
 * - useCreatePrePrintBatch: create a pre-print batch with cache invalidation
 * - usePrePrintBatchList: list pre-print batches with pagination
 */

import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { parsePrePrintDtfRefusal, parseSupplyRefusal } from '@/lib/trpc-error-utils';
import { preprintRefusedMessage } from '@/features/inventory/utils/supplyAliasCopy';

// ============================================================================
// PRODUCT SEARCH
// ============================================================================

/**
 * Search Shopify products via the Shopify Admin GraphQL API.
 *
 * Returns products with nested variants from `trpc.prePrint.searchShopifyProducts`.
 * Searches against Shopify directly so all products (including POD) are searchable,
 * not just PAP-tagged ones in the local products table.
 *
 * Enabled only when search has 2+ characters to avoid excessive queries.
 */
export function useProductSearch(search: string) {
  const query = trpc.prePrint.searchShopifyProducts.useQuery(
    { search },
    {
      enabled: search.length >= 2,
      staleTime: 30_000,
      placeholderData: (prev) => prev,
    }
  );

  return {
    products: query.data ?? [],
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
  };
}

// ============================================================================
// BLANK SKU MATCHING
// ============================================================================

/**
 * Find the matching blank garment SKU for a product SKU at a location.
 *
 * Returns `{ match, isLoading }` where match is `BlankSkuMatch | null`.
 * Enabled only when both locationId and productSku are non-empty.
 *
 * IMPORTANT: This hook must be called in a stable component (not inside .map()).
 * Create a child component per selected item that calls this hook once.
 */
export function useBlankSkuMatch(
  locationId: string,
  productSku: string,
  fallbackAttrs?: { productCategory?: string; productSize?: string; productColor?: string }
) {
  const query = trpc.prePrint.getMatchingBlankSku.useQuery(
    {
      locationId,
      productSku,
      productCategory: fallbackAttrs?.productCategory,
      productSize: fallbackAttrs?.productSize,
      productColor: fallbackAttrs?.productColor,
    },
    {
      enabled: locationId.length > 0 && productSku.length > 0,
      staleTime: 60_000,
      retry: 1,
    }
  );

  return {
    match: query.data ?? null,
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    error: query.error,
  };
}

// ============================================================================
// CREATE BATCH
// ============================================================================

/**
 * Mutation hook to create a pre-print batch.
 *
 * On success: invalidates batch list queries and shows a success toast.
 * Returns the full tRPC mutation object.
 *
 * `onOpenFilmPreprint` (optional): when given, the DTF refusal toast carries
 * an "Open Pre-Print Films" action that calls it. The hook takes no router
 * dependency; the page supplies the navigation.
 */
export function useCreatePrePrintBatch(options: { onOpenFilmPreprint?: () => void } = {}) {
  const { onOpenFilmPreprint } = options;
  const utils = trpc.useUtils();
  const { t } = useTranslation('inventory');
  const { t: tProduction } = useTranslation('production');

  return trpc.prePrint.createBatch.useMutation({
    onSuccess: (data) => {
      utils.productionOrders.listBatches.invalidate();
      // Phase 92 (D-13, UI-SPEC Screen 4 R4 batch): supply SKUs were left out
      // of the batch because they print to order on a shared blank.
      const skippedSupply = data.skippedSupply ?? 0;
      const garments = `${data.totalGarments} garments queued for printing`;
      toast.success(`Pre-print batch #${data.batchNumber} created`, {
        description:
          skippedSupply > 0
            ? `${garments}. ${t('sharedSkus.supply.preprintBatch', { count: skippedSupply })}`
            : garments,
      });
    },
    onError: (error) => {
      // BLU-1420: garment pre-print is DTG only; a DTF item belongs on Pre-Print Films.
      const dtfRefusal = parsePrePrintDtfRefusal(error);
      if (dtfRefusal) {
        toast.error('Failed to create pre-print batch', {
          description: tProduction('preprint.dtfRefused', {
            count: dtfRefusal.count,
            sku: dtfRefusal.sku ?? '',
          }),
          ...(onOpenFilmPreprint
            ? { action: { label: tProduction('preprint.openFilmPreprint'), onClick: onOpenFilmPreprint } }
            : {}),
        });
        return;
      }
      // Every item was a supply SKU: nothing to pre-print (R4 single).
      const refusal = parseSupplyRefusal(error);
      if (refusal?.kind === 'preprint_refused') {
        toast.error('Failed to create pre-print batch', {
          description: preprintRefusedMessage(t, { sku: refusal.sku ?? '', ownerName: refusal.ownerName }),
        });
        return;
      }
      toast.error('Failed to create pre-print batch', {
        description: error.message,
      });
    },
  });
}

// ============================================================================
// BATCH LIST
// ============================================================================

/**
 * List pre-print batches with pagination.
 *
 * Wraps `trpc.productionOrders.listBatches` with `batchType: 'pre_print'`.
 */
export function usePrePrintBatchList(
  locationId: string | undefined,
  page: number = 0,
  pageSize: number = 20,
) {
  const offset = page * pageSize;

  const query = trpc.productionOrders.listBatches.useQuery(
    {
      locationId: locationId!,
      batchType: 'pre_print',
      sortOrder: 'newest',
      limit: pageSize,
      offset,
    },
    {
      enabled: !!locationId,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );

  return {
    batches: query.data?.batches ?? [],
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    totalCount: query.data?.pagination?.total ?? 0,
    totalPages: Math.ceil((query.data?.pagination?.total ?? 0) / pageSize),
  };
}
